export const LuiV3BaseColors = [
  'gray',
  'blue',
  'green',
  'lime',
  'orange',
  'pink',
  'purple',
  'red',
  'teal',
  'yellow',
  'slate',
] as const;

export const LuiV3ColorIndexes = [
  '0',
  '25',
  '37-5',
  '50',
  '100',
  '150',
  '200',
  '300',
  '400',
  '500',
  '600',
  '700',
  '800',
  '900',
  '1000',
] as const;

export type LuiV3ColorName = (typeof LuiV3BaseColors)[number];

export type LuiV3ColorIndex = (typeof LuiV3ColorIndexes)[number];

export type LuiV3ColorKey = `${LuiV3ColorName}-${LuiV3ColorIndex}`;

const Luiv3Constants: {
  baseColor: {
    [key in LuiV3ColorKey]: string;
  };
} = {
  baseColor: {
    'gray-1000': '#03060C',
    'gray-900': '#1C1F24',
    'gray-800': '#35383D',
    'gray-700': '#4F5155',
    'gray-600': '#686A6D',
    'gray-500': '#818285',
    'gray-400': '#9A9B9E',
    'gray-300': '#B3B4B6',
    'gray-200': '#CDCDCE',
    'gray-150': '#D9DADB',
    'gray-100': '#E6E6E7',
    'gray-50': '#F2F2F3',
    'gray-37-5': '#F6F6F7',
    'gray-25': '#F9F9F9',
    'gray-0': '#FFFFFF',
    'blue-1000': '#03060C', // same as gray-1000
    'blue-900': '#101B3D',
    'blue-800': '#1D306D',
    'blue-700': '#2B469E',
    'blue-600': '#385BCE',
    'blue-500': '#4570FF',
    'blue-400': '#6A8DFF',
    'blue-300': '#8FA9FF',
    'blue-200': '#B5C6FF',
    'blue-150': '#C7D4FF',
    'blue-100': '#DAE2FF',
    'blue-50': '#ECF1FF',
    'blue-37-5': '#F1F5FF',
    'blue-25': '#F5F8FF',
    'blue-0': '#FFFFFF', // same as gray-0
    'green-1000': '#03060C', // same as gray-1000
    'green-900': '#0C261F',
    'green-800': '#154732',
    'green-700': '#1D6745',
    'green-600': '#268858',
    'green-500': '#2FA86B',
    'green-400': '#59B989',
    'green-300': '#82CBA6',
    'green-200': '#ACDCC4',
    'green-150': '#C1E5D3',
    'green-100': '#D5EEE1',
    'green-50': '#EAF6F0',
    'green-37-5': '#EFF8F4',
    'green-25': '#F5FBF7',
    'green-0': '#FFFFFF', // same as gray-0
    'lime-1000': '#03060C', // same as gray-1000
    'lime-900': '#212C15',
    'lime-800': '#3E521E',
    'lime-700': '#5C7928',
    'lime-600': '#799F31',
    'lime-500': '#97C53A',
    'lime-400': '#ACD161',
    'lime-300': '#C1DC89',
    'lime-200': '#D5E8B0',
    'lime-150': '#E0EEC4',
    'lime-100': '#EAF3D8',
    'lime-50': '#F5F9EB',
    'lime-37-5': '#F7FBF0',
    'lime-25': '#FAFCF5',
    'lime-0': '#FFFFFF', // same as gray-0
    'orange-1000': '#03060C', // same as gray-1000
    'orange-900': '#332013',
    'orange-800': '#633B1A',
    'orange-700': '#945520',
    'orange-600': '#C47027',
    'orange-500': '#F48A2E',
    'orange-400': '#F6A158',
    'orange-300': '#F8B982',
    'orange-200': '#FBD0AB',
    'orange-150': '#FCDCC0',
    'orange-100': '#FDE8D5',
    'orange-50': '#FEF3EA',
    'orange-37-5': '#FEF6EF',
    'orange-25': '#FFF9F5',
    'orange-0': '#FFFFFF', // same as gray-0
    'pink-1000': '#03060C', // same as gray-1000
    'pink-900': '#331A2B',
    'pink-800': '#632F4A',
    'pink-700': '#924369',
    'pink-600': '#C25888',
    'pink-500': '#F26CA7',
    'pink-400': '#F589B9',
    'pink-300': '#F7A7CA',
    'pink-200': '#FAC4DC',
    'pink-150': '#FBD3E5',
    'pink-100': '#FCE2ED',
    'pink-50': '#FEF0F6',
    'pink-37-5': '#FEF4F8',
    'pink-25': '#FFF7FB',
    'pink-0': '#FFFFFF', // same as gray-0
    'purple-1000': '#03060C', // same as gray-1000
    'purple-900': '#1C1638',
    'purple-800': '#342664',
    'purple-700': '#4D3690',
    'purple-600': '#6546BC',
    'purple-500': '#7E56E8',
    'purple-400': '#9878ED',
    'purple-300': '#B29AF1',
    'purple-200': '#CBBBF6',
    'purple-150': '#D8CCF8',
    'purple-100': '#E5DDFA',
    'purple-50': '#F2EEFD',
    'purple-37-5': '#F5F2FD',
    'purple-25': '#F9F7FE',
    'purple-0': '#FFFFFF', // same as gray-0
    'red-1000': '#03060C', // same as gray-1000
    'red-900': '#2C1119',
    'red-800': '#561C26',
    'red-700': '#7F2832',
    'red-600': '#A9333F',
    'red-500': '#D23E4C',
    'red-400': '#DB6570',
    'red-300': '#E48B94',
    'red-200': '#EDB2B7',
    'red-150': '#F1C5C9',
    'red-100': '#F6D8DB',
    'red-50': '#FAECED',
    'red-37-5': '#FBF1F1',
    'red-25': '#FDF5F6',
    'red-0': '#FFFFFF', // same as gray-0
    'teal-1000': '#03060C', // same as gray-1000
    'teal-900': '#0A282F',
    'teal-800': '#114A51',
    'teal-700': '#196D74',
    'teal-600': '#208F96',
    'teal-500': '#27B1B9',
    'teal-400': '#52C1C7',
    'teal-300': '#7DD0D5',
    'teal-200': '#A9E0E3',
    'teal-150': '#BEE8EA',
    'teal-100': '#D4EFF1',
    'teal-50': '#E9F7F8',
    'teal-37-5': '#EFF9FA',
    'teal-25': '#F4FBFB',
    'teal-0': '#FFFFFF', // same as gray-0
    'yellow-1000': '#03060C', // same as gray-1000
    'yellow-900': '#332A13',
    'yellow-800': '#624E1A',
    'yellow-700': '#927221',
    'yellow-600': '#C19628',
    'yellow-500': '#F1BA2F',
    'yellow-400': '#F4C859',
    'yellow-300': '#F7D682',
    'yellow-200': '#F9E3AC',
    'yellow-150': '#FBEAC1',
    'yellow-100': '#FCF1D5',
    'yellow-50': '#FEF8EA',
    'yellow-37-5': '#FEFAEF',
    'yellow-25': '#FFFBF5',
    'yellow-0': '#FFFFFF', // same as gray-0
    'slate-1000': '#03060C', // same as gray-1000
    'slate-900': '#161D31',
    'slate-800': '#293455',
    'slate-700': '#3D4C7A',
    'slate-600': '#50639D',
    'slate-500': '#6379C2',
    'slate-400': '#8294CF',
    'slate-300': '#A1AFDB',
    'slate-200': '#C1C9E7',
    'slate-150': '#D0D7ED',
    'slate-100': '#E0E4F3',
    'slate-50': '#EFF1F9',
    'slate-37-5': '#F3F5FB',
    'slate-25': '#F5F7FB',
    'slate-0': '#FFFFFF', // same as gray-0
  },
};

export const CHART_COLOR_KEYS: readonly LuiV3ColorKey[] = [
  'green-500',
  'yellow-500',
  'orange-500',
  'purple-500',
  'red-500',
  'lime-500',
  'blue-400',
  'teal-400',
  'gray-500',
  'pink-500',
  'green-600',
  'yellow-600',
  'orange-600',
  'purple-600',
  'red-600',
  'lime-600',
  'blue-600',
  'teal-600',
  'gray-600',
  'pink-600',
];

export const CHART_COLORS: readonly string[] = CHART_COLOR_KEYS.map(
  (key) => Luiv3Constants.baseColor[key]
);

export const chartColor = (index: number): string =>
  CHART_COLORS[index % CHART_COLORS.length];

export const chartStrokeColor = (index: number): string => {
  const key = CHART_COLOR_KEYS[index % CHART_COLOR_KEYS.length];
  const dash = key.lastIndexOf('-');
  const name = key.slice(0, dash) as LuiV3ColorName;
  const shade = Number(key.slice(dash + 1));
  const strokeShade = shade <= 400 ? '600' : shade <= 500 ? '700' : '800';
  return Luiv3Constants.baseColor[`${name}-${strokeShade}` as LuiV3ColorKey];
};

export default Luiv3Constants;
